import type { OrgAuditEvent } from "./api.js";
import { roleAtLeast, type OrgRole } from "./orgs.js";

/**
 * What the audit screen says, decided away from React (LGN-146).
 *
 * Same split as `orgs.ts` and `billing.ts`: the screen renders, this file
 * decides. Everything here is a pure function of what the API returned, which
 * is what lets the gate, the paging sentence and the actor labels be tested in
 * the repository's node environment rather than through a rendered DOM.
 *
 * The screen is a reader and nothing else. `audit_events` is append-only by
 * construction (INSERT-only grants, plus a trigger that rejects UPDATE, DELETE
 * and TRUNCATE whoever asks), so there is no endpoint here that could edit a
 * row and this file offers no action that would want one.
 */

/**
 * How many rows one page asks for.
 *
 * The route's own default, and well under its ceiling of 200. Named here
 * because the screen has to say the number out loud: a full page is a capped
 * page, and a capped page shown without saying so implies the cap is the whole
 * answer (LGN-125 learned that in the console).
 */
export const AUDIT_PAGE_SIZE = 50;

/**
 * Whether this person can open the audit trail at all, and if not, why.
 *
 * Two gates, in the order the platform applies them. The role first, because
 * it is known the moment the org is: the trail names who invited whom and who
 * removed whom, and a member reading the audit row for their own removal is
 * not a feature. Then the plan, which needs the entitlements query to have
 * answered.
 *
 * `undefined` entitlements is `loading` rather than a refusal, so a slow
 * request never briefly tells somebody their plan does not include something
 * it does.
 *
 * A courtesy, not a control. `GET /api/orgs/:orgId/audit` decides both again,
 * and an admin who edits their own JavaScript gets a 403 carrying the
 * platform's own sentence. What this buys is a rail that does not advertise a
 * screen the plan does not include, and a URL that explains itself instead of
 * drawing a table and then refusing to fill it.
 */
export type AuditAccess = "ok" | "loading" | "not-permitted" | "not-entitled";

export function auditAccess(
  role: OrgRole,
  entitlements: { auditExport: boolean } | undefined,
): AuditAccess {
  if (!roleAtLeast(role, "admin")) return "not-permitted";
  if (entitlements === undefined) return "loading";
  return entitlements.auditExport ? "ok" : "not-entitled";
}

/** Whether the rail carries an Audit item. Only ever when the screen works. */
export function showAuditLink(
  role: OrgRole,
  entitlements: { auditExport: boolean } | undefined,
): boolean {
  return auditAccess(role, entitlements) === "ok";
}

/**
 * The sentence under a page of rows.
 *
 * Its whole job is to stop a capped page reading as a complete one. A page
 * that came back with a cursor is a page the platform truncated, and saying
 * "50 entries" without saying "of more" is the failure LGN-125 found in the
 * console: the reader takes the cap for the answer and stops looking.
 */
export function describePage(input: {
  count: number;
  nextBefore: number | null;
  /** Zero-based: 0 is the newest page. */
  page: number;
}): string {
  const entries = `${String(input.count)} ${input.count === 1 ? "entry" : "entries"}`;
  const position = input.page === 0 ? "The newest" : `Page ${String(input.page + 1)}:`;
  if (input.nextBefore !== null) {
    return `${position} ${entries}, which is all this page holds. There are older ones: choose Older to keep reading.`;
  }
  return input.page === 0
    ? `${entries}, and nothing older.`
    : `${position} ${entries}. This is the oldest page.`;
}

/**
 * Who did it, in words the organisation can use.
 *
 * An address when the organisation's own person did it. Otherwise a role,
 * because the platform withholds the address: naming the individual member of
 * Tech Ops who touched an account tells the customer nothing they can act on
 * and hands them a person to chase.
 *
 * The product name is passed in rather than written here, so a rename stays a
 * change to `branding.ts` (`no-brand-literals.test.ts` enforces it).
 */
export function describeActor(event: OrgAuditEvent, productName: string): string {
  if (event.actorEmail !== null) return event.actorEmail;
  switch (event.actorType) {
    case "staff":
    case "admin":
      return `${productName} support`;
    case "billing":
      return "Billing";
    case "system":
      return "Automatic";
    case "viewer":
      return "A site visitor";
    default:
      return "Someone in this organisation";
  }
}

/**
 * What happened, in a sentence, where the action code has a known meaning.
 *
 * Null rather than a prettified version of the code when it does not: turning
 * `screening.retro_blocked` into "Screening retro blocked" reads like a
 * sentence somebody wrote and means less than the code does. The code is shown
 * either way, so nothing is lost by staying quiet here.
 */
const ACTION_LABELS: Readonly<Record<string, string | undefined>> = {
  "org.create": "This organisation was created.",
  "org.rename": "The organisation was renamed.",
  "org.invite": "Somebody was invited to the organisation.",
  "org.invite_revoke": "An invitation was withdrawn.",
  "org.member_join": "Somebody accepted an invitation and joined.",
  "org.member_remove": "Somebody was removed from the organisation.",
  "org.role_change": "Somebody's role was changed.",
  "org.provision": "This organisation was set up for you.",
  "org.suspend": "The organisation was suspended.",
  "org.unsuspend": "The suspension was lifted.",
  "org.delete": "Deletion of the organisation was requested.",
  "site.create": "A site was created.",
  "site.delete": "A site was deleted.",
  "site.suspend": "A site was suspended.",
  "site.unsuspend": "A site's suspension was lifted.",
  "site.unpublish": "A site was taken off the air.",
  "site.public_mode_revoked": "A site's public mode was withdrawn.",
  "group.create": "A group was created.",
  "group.delete": "A group was deleted.",
  "group.member_add": "Somebody was added to a group.",
  "group.member_remove": "Somebody was removed from a group.",
  "allowlist.add": "An address was allowed at a site.",
  "allowlist.remove": "An address was removed from a site.",
  "allowlist.domain_add": "A domain was allowed at a site.",
  "allowlist.domain_remove": "A domain was removed from a site.",
  "guest_pass.create": "A guest link was created.",
  "guest_pass.revoke": "A guest link was revoked.",
  "quota.update": "A limit on this organisation was changed.",
  "billing.override": "Support adjusted this organisation's limits by hand.",
  "billing.override_clear": "A hand-set limit was lifted and the plan's own restored.",
};

export function describeAction(action: string): string | null {
  return ACTION_LABELS[action] ?? null;
}

/**
 * Whether any row on this page had something held back.
 *
 * Drives one footnote under the list rather than a marker on every operator
 * row. The customer is owed the fact that operator rows are not shown in full;
 * they are not owed it forty times on one page.
 */
export function anyWithheld(events: readonly OrgAuditEvent[]): boolean {
  return events.some((event) => event.withheld);
}
